import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

interface BulkTaskData {
  title: string;
  description?: string;
  priority: string;
  status?: string;
  due_date?: string;
  project_id?: string;
  lead_id?: string;
}

export const useTaskBulkOperations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const [isCreating, setIsCreating] = useState(false);

  const createTasksWithAssignments = async (
    tasks: BulkTaskData[],
    assignments: Record<string, string>
  ) => {
    if (!currentOrganization?.id) {
      throw new Error("No organization selected");
    }

    setIsCreating(true);
    const createdTasks = [];
    const errors = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user");

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const assignedTo = assignments[i] || null;

        try {
          const taskData = {
            ...task,
            assigned_to: assignedTo || null,
            status: task.status || 'todo',
            organization_id: currentOrganization.id,
            created_by: user.id
          };

          const { error } = await supabase.functions.invoke("tasks-operations", {
            body: {
              action: "create",
              task: taskData,
              organizationId: currentOrganization.id,
              userId: user.id
            }
          });

          if (error) {
            throw error;
          }

          createdTasks.push(task.title);
        } catch (error) {
          console.error(`Error creating task "${task.title}":`, error);
          errors.push(`${task.title}: ${error.message}`);
        }
      }

      // Refresh tasks list
      queryClient.invalidateQueries({ queryKey: ["tasks"] });

      // Show results
      if (createdTasks.length > 0) {
        toast({
          title: "Tasks Created Successfully",
          description: `${createdTasks.length} task(s) created${
            errors.length > 0 ? ` (${errors.length} failed)` : ""
          }`,
        });
      }

      if (errors.length > 0) {
        console.error("Task creation errors:", errors);
        toast({
          title: "Some Tasks Failed",
          description: `${errors.length} task(s) could not be created`,
          variant: "destructive",
        });
      }

      return {
        success: createdTasks.length,
        errors: errors.length,
        total: tasks.length
      };

    } catch (error) {
      console.error("Bulk task creation error:", error);
      toast({
        title: "Error",
        description: "Failed to create tasks",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createTasksWithAssignments,
    isCreating
  };
};